import { toDisplayMediaUrl, toProxyMediaUrl } from '@/lib/media-url';
import { translateClient } from '@/lib/i18n/client';

const ASSET_PROXY_DOWNLOAD_PATH = '/api/assets/proxy/download';
const PNG_MIME_TYPE = 'image/png';

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function replaceFilenameExtension(filename: string, extension: string): string {
  const trimmed = filename.trim() || `download-${Date.now()}`;
  const safeExtension = extension.replace(/^\./, '').toLowerCase();
  if (!safeExtension) return trimmed;
  const base = trimmed.replace(/\.[a-z0-9]{2,8}$/i, '');
  return `${base}.${safeExtension}`;
}

export function withPngDownloadExtension(filename: string): string {
  return replaceFilenameExtension(filename, 'png');
}

function unwrapAssetProxyDownloadUrl(url: string): string {
  try {
    const parsed = new URL(url, 'https://octolab.local');
    if (parsed.pathname !== ASSET_PROXY_DOWNLOAD_PATH && parsed.pathname !== '/api/auth/media/assets/proxy/download') {
      return url;
    }
    return parsed.searchParams.get('url')?.trim() || url;
  } catch {
    return url;
  }
}

function stripCosImageTransform(url: string): string {
  if (!url || /^(?:blob:|data:)/i.test(url)) return url;

  try {
    const isAbsolute = /^https?:\/\//i.test(url);
    const parsed = new URL(url, isAbsolute ? undefined : 'https://octolab.local');
    const queryParts = parsed.search ? parsed.search.slice(1).split('&') : [];
    if (queryParts.length === 0) return url;

    const filteredParts = queryParts.filter((part) => {
      const decodedPart = safeDecodeURIComponent(part);
      return !decodedPart.startsWith('imageMogr2/')
        && !decodedPart.startsWith('imageMogr2=')
        && !decodedPart.startsWith('ci-process=');
    });

    if (filteredParts.length === queryParts.length) return url;
    parsed.search = filteredParts.length > 0 ? `?${filteredParts.join('&')}` : '';
    return isAbsolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url.replace(/([?&])(?:imageMogr2\/[^&#]*|imageMogr2=[^&#]*|ci-process=[^&#]*)(?=&|#|$)/gi, '$1')
      .replace(/[?&](?=#|$)/, '');
  }
}

export function getOriginalMediaDownloadUrl(url: string): string {
  return stripCosImageTransform(unwrapAssetProxyDownloadUrl(url));
}

export function withOriginalDownloadExtension(
  filename: string,
  sourceUrl?: string | null,
  mimeType?: string | null,
): string {
  void sourceUrl;
  void mimeType;
  return withPngDownloadExtension(filename);
}

async function drawImageBlobToPng(blob: Blob): Promise<Blob> {
  if (typeof document === 'undefined') {
    throw new Error('Image download conversion requires a browser context');
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not prepare image download');
  }

  const drawWithImageElement = async () => {
    const objectUrl = URL.createObjectURL(blob);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Could not decode image download'));
        img.src = objectUrl;
      });
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      ctx.drawImage(image, 0, 0);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  };

  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close();
    } catch {
      await drawWithImageElement();
    }
  } else {
    await drawWithImageElement();
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((pngBlob) => {
      if (pngBlob) {
        resolve(pngBlob);
      } else {
        reject(new Error('Could not encode PNG download'));
      }
    }, PNG_MIME_TYPE);
  });
}

export async function prepareImageDownloadBlob(blob: Blob): Promise<Blob> {
  if (blob.type.split(';')[0]?.trim().toLowerCase() === PNG_MIME_TYPE) {
    return blob;
  }

  const pngBlob = await drawImageBlobToPng(blob);
  if (pngBlob.type.split(';')[0]?.trim().toLowerCase() !== PNG_MIME_TYPE) {
    return new Blob([pngBlob], { type: PNG_MIME_TYPE });
  }
  return pngBlob;
}

export async function fetchMediaDownloadResponse(url: string): Promise<Response> {
  if (!url) {
    throw new Error('Missing download URL');
  }

  let downloadUrl = getOriginalMediaDownloadUrl(url);
  const parsed = new URL(url, 'https://octolab.local');
  if (parsed.pathname === ASSET_PROXY_DOWNLOAD_PATH || parsed.pathname === '/api/auth/media/assets/proxy/download') {
    // A scoped media token belongs to the wrapper, not its nested storage URL.
    // Preserve it while removing thumbnail transforms from the inner URL.
    parsed.searchParams.set('url', downloadUrl);
    downloadUrl = `${parsed.pathname}${parsed.search}`;
  }

  // Owned media uses the authenticated same-origin bridge. Cross-origin <img>
  // display does not imply that browser fetch is allowed by the storage CORS.
  return fetch(toProxyMediaUrl(toDisplayMediaUrl(downloadUrl)), { credentials: 'same-origin' });
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

export function triggerDirectDownload(url: string, filename?: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  if (filename) anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export async function resolveImageDownloadBlob(source: Blob | string): Promise<Blob> {
  if (source instanceof Blob) {
    return prepareImageDownloadBlob(source);
  }

  const response = await fetchMediaDownloadResponse(source);
  if (!response.ok) {
    throw new Error(translateClient('workflow', 'downloadFailedSafe'));
  }

  return prepareImageDownloadBlob(await response.blob());
}

export async function downloadImageUrl(url: string, filename: string): Promise<void> {
  const response = await fetchMediaDownloadResponse(url);
  if (!response.ok) {
    throw new Error(translateClient('workflow', 'downloadFailedSafe'));
  }

  const blob = await prepareImageDownloadBlob(await response.blob());
  triggerBlobDownload(blob, withPngDownloadExtension(filename));
}

export async function downloadMediaUrl(url: string, filename: string): Promise<void> {
  const response = await fetchMediaDownloadResponse(url);
  if (!response.ok) {
    throw new Error(translateClient('workflow', 'downloadFailedSafe'));
  }

  const blob = await response.blob();
  triggerBlobDownload(blob, filename);
}
